import { Link, useParams } from "react-router-dom";
import PageHero from "@/components/base/PageHero";
import EpisodeCard from "@/components/base/EpisodeCard";
import FavoriteButton from "@/components/base/FavoriteButton";
import { episodes } from "@/mocks/episodes";
import { useSeo } from "@/hooks/useSeo";

export default function EpisodeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const episode = episodes.find((ep) => ep.id === id);

  useSeo({
    title: episode
      ? `第${episode.number}集《${episode.title}》剧情介绍 - 年轻的母亲2`
      : "剧集未找到 - 年轻的母亲2",
    description: episode
      ? `《年轻的母亲2》第${episode.number}集《${episode.title}》剧情介绍：${episode.summary}`
      : "未找到对应的剧集内容，请返回剧集列表查看全部剧集。",
    keywords: episode
      ? `年轻的母亲2第${episode.number}集,${episode.title},剧情介绍,分集剧情`
      : "年轻的母亲2,剧集列表",
  });

  if (!episode) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-24 text-center md:px-6">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-emotion-sad-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这一集</h1>
        <p className="mt-3 text-sm text-foreground-600">该剧集可能已调整或链接有误，返回剧集列表继续浏览吧。</p>
        <Link
          to="/episodes"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-semibold text-background-50"
        >
          返回剧集列表
        </Link>
      </div>
    );
  }

  const related = episodes
    .filter((ep) => Math.abs(ep.number - episode.number) <= 2 && ep.id !== episode.id)
    .sort((a, b) => Math.abs(a.number - episode.number) - Math.abs(b.number - episode.number))
    .slice(0, 3);

  const metaItems = [
    { icon: "ri-play-list-2-line", label: "集数", value: `第 ${episode.number} 集` },
    { icon: "ri-calendar-line", label: "首播日期", value: episode.airDate },
    { icon: "ri-clapperboard-line", label: "导演", value: episode.director },
    { icon: "ri-quill-pen-line", label: "编剧", value: episode.writers },
    { icon: "ri-user-star-line", label: "主要演员", value: episode.cast },
  ];

  return (
    <div className="w-full">
      <PageHero
        eyebrow={`第 ${episode.number} 集`}
        title={episode.title}
        desc={episode.summary}
        breadcrumb={[
          { label: "首页", to: "/" },
          { label: "剧集列表", to: "/episodes" },
          { label: `第 ${episode.number} 集 ${episode.title}` },
        ]}
      />

      <article className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            <div className="w-full overflow-hidden rounded-lg border border-background-200 bg-secondary-200">
              <div className="h-[240px] w-full sm:h-[360px] md:h-[440px]">
                <img
                  src={episode.cover}
                  alt={`《年轻的母亲2》第${episode.number}集《${episode.title}》剧照`}
                  title={`第${episode.number}集 ${episode.title} - 年轻的母亲2 剧照`}
                  className="h-full w-full object-top"
                />
              </div>
            </div>

            <div className="mt-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                <span className="h-5 w-1 rounded-full bg-accent-500"></span>
                剧情介绍
              </h2>
              <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
                {episode.content}
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <FavoriteButton id={episode.id} />
              <div className="flex flex-wrap gap-2">
                {episode.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-900"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-information-line text-lg"></i>
                </span>
                剧集信息
              </h2>
              <dl className="mt-4 flex flex-col gap-3">
                {metaItems.map((item) => (
                  <div key={item.label} className="flex items-start gap-3 border-b border-background-200 pb-3 last:border-none last:pb-0">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${item.icon} text-base`}></i>
                    </span>
                    <div>
                      <dt className="font-label text-xs text-foreground-500">{item.label}</dt>
                      <dd className="mt-0.5 text-sm font-medium text-foreground-900">{item.value}</dd>
                    </div>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-5">
              <p className="font-label text-xs font-semibold uppercase tracking-wide text-accent-700">
                追剧提示
              </p>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                点击右侧“收藏本集”，即可在浏览器本地记住这一集，刷新页面后依然保留（仅作演示）。
              </p>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
              <span className="h-5 w-1 rounded-full bg-primary-500"></span>
              相关剧集推荐
            </h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((ep) => (
                <EpisodeCard key={ep.id} {...ep} />
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
}